import React, { useEffect } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { Megaphone, Plus, Calendar, Home, Edit, Eye, Trash2 } from 'lucide-react';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';
import apiClient from '@/services/apiClient';

export const VacancyList: React.FC = () => {
    const { vacancies, fetchVacancies, loading } = useLandlordStore();
    const navigate = useNavigate();
    const { success, error: toastError, warning } = useToast();

    useEffect(() => {
        fetchVacancies();
    }, []);

    const handlePostVacancy = () => {
        navigate('/landlord/vacancies/new');
    };

    const handleEditVacancy = (vacancyId: string) => {
        navigate(`/landlord/vacancies/edit/${vacancyId}`);
    };

    const handlePreviewVacancy = (vacancy: any) => {
        navigate('/landlord/vacancies/preview', { 
            state: { 
                vacancy: {
                    ...vacancy,
                    isEdit: true,
                } 
            } 
        });
    };

    const handleDeleteVacancy = async (vacancyId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this vacancy?')) {
            return;
        }

        try {
            await apiClient.delete(`/vacancies/${vacancyId}`);
            success('Vacancy deleted', 'The vacancy has been removed successfully.');
            fetchVacancies();
        } catch (error) {
            console.error('Failed to delete vacancy:', error);
            toastError('Delete failed', 'Failed to delete vacancy. Please try again.');
        }
    };

    const handleManageListing = (vacancy: any) => {
        // Open a dropdown or modal with management options
        handleEditVacancy(vacancy.id);
    };

    if (loading && vacancies.length === 0) return <LoadingScreen />;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">Marketing & Vacancies</h1>
                    <p className="text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle mt-1">Manage your property listings and advertisements</p>
                </div>
                <button
                    onClick={handlePostVacancy}
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
                >
                    <Plus size={20} />
                    Post Vacancy
                </button>
            </div>

            {vacancies.length === 0 ? (
                <div className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-12 rounded-lg border border-dashed border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border text-center">
                    <Megaphone className="mx-auto h-12 w-12 text-light-subtle dark:text-dark-subtle mb-4" />
                    <h3 className="text-lg font-medium text-light-text dark:text-dark-text dark:text-white">No active listings</h3>
                    <p className="text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle">Create a listing to fill your vacant units.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {vacancies.map((vacancy) => (
                        <div key={vacancy.id} className="bg-light-card dark:bg-dark-card dark:bg-dark-card rounded-xl overflow-hidden shadow-sm border border-light-border dark:border-dark-border group hover:border-primary transition-colors">
                            <div className="h-48 bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 relative">
                                {vacancy.images?.[0] ? (
                                    <img src={vacancy.images[0]} alt={vacancy.title} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-light-subtle dark:text-dark-subtle">
                                        <Home size={48} />
                                    </div>
                                )}
                                <div className="absolute top-2 right-2 px-2 py-1 bg-light-card dark:bg-dark-card/90 rounded text-xs font-bold text-light-text dark:text-dark-text">
                                    {vacancy.status}
                                </div>
                            </div>
                            <div className="p-5">
                                <h3 className="text-lg font-bold text-light-text dark:text-dark-text dark:text-white mb-2">{vacancy.title}</h3>
                                <p className="text-primary font-bold text-xl mb-4">KES {vacancy.rent_amount.toLocaleString()}<span className="text-sm text-light-subtle dark:text-dark-subtle font-normal">/mo</span></p>

                                <div className="flex items-center text-light-subtle dark:text-dark-subtle text-sm mb-4">
                                    <Calendar size={16} className="mr-2" />
                                    <span>Available: {new Date(vacancy.available_date).toLocaleDateString()}</span>
                                </div>

                                <div className="space-y-2">
                                    <button 
                                        onClick={() => handleManageListing(vacancy)}
                                        className="w-full py-2 border border-light-border dark:border-dark-border dark:border-gray-600 rounded-lg hover:bg-light-bg dark:bg-dark-bg dark:hover:bg-gray-100 dark:bg-gray-800 transition-colors text-gray-700 dark:text-gray-300 font-medium flex items-center justify-center gap-2"
                                    >
                                        <Edit size={16} />
                                        Edit Listing
                                    </button>
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={() => handlePreviewVacancy(vacancy)}
                                            className="flex-1 py-2 border border-primary text-primary rounded-lg hover:bg-primary/10 transition-colors font-medium flex items-center justify-center gap-1 text-sm"
                                        >
                                            <Eye size={14} />
                                            Preview
                                        </button>
                                        <button 
                                            onClick={(e) => handleDeleteVacancy(vacancy.id, e)}
                                            className="flex-1 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors font-medium flex items-center justify-center gap-1 text-sm"
                                        >
                                            <Trash2 size={14} />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
